<script setup lang="ts">
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import FileUpload from '@/components/Applications/Shared/FileUpload.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// Track whether the file was provided when "ekki greiddan" is selected
const fileValidation = reactive({
  filerequired: false
})

// Validation:
// - radio choice required
// - file required only when "ekki greiddan" is selected
const rules = {
  umsokn: {
    greiddanLifeyrir: { required }
  },
  fileValidation: {
    filerequired: {
      required: requiredIf(() =>
        umsokn.value?.greiddanLifeyrir === t('utgreidsla_umsokn_stadfestingororku_ekki_greiddan_ororku')
      )
    }
  }
}
const v$ = useVuelidate(rules, { umsokn, fileValidation }, { $autoDirty: true })

// Hook for FileUpload to mark success (adjust event name to your component)
function onFileUploaded(ok: boolean) {
  fileValidation.filerequired = !!ok
}

// Called by ApplicationBase
async function validate(): Promise<boolean> {
  await v$.value.$validate()
  return !v$.value.$error
}

defineExpose({ validate })
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <v-row>
        <v-col>
          <h2 class="forms__header">
            {{ t('utgreidsla_umsokn_stadfestingororku_header1') }}
          </h2>
        </v-col>
      </v-row>

      <!-- Radios -->
      <v-row>
        <v-col cols="12">
          <v-radio-group
            v-model="umsokn.greiddanLifeyrir"
            :readonly="props.disable"
            hide-details
            :error="v$.umsokn.greiddanLifeyrir.$error"
            :error-messages="v$.umsokn.greiddanLifeyrir.$error ? [t('common.val_required')] : []"
          >
            <v-radio
              :label="t('utgreidsla_umsokn_stadfestingororku_greiddan_ororku')"
              :value="t('utgreidsla_umsokn_stadfestingororku_greiddan_ororku')"
            />
            <v-radio
              :label="t('utgreidsla_umsokn_stadfestingororku_ekki_greiddan_ororku')"
              :value="t('utgreidsla_umsokn_stadfestingororku_ekki_greiddan_ororku')"
            />
          </v-radio-group>
        </v-col>
      </v-row>

      <!-- Conditional file upload when not receiving disability payments -->
      <v-row
        v-if="umsokn.greiddanLifeyrir === t('utgreidsla_umsokn_stadfestingororku_ekki_greiddan_ororku')"
        class="mt-4"
      >
        <v-col cols="12">
          <p class="forms__header m-t-20" style="font-size: 18px">
            {{ t('utgreidsla_umsokn_stadfestingororku_file_header') }}
          </p>

          <!-- Adjust props to your FileUpload API. Using filespace=3 like legacy. -->
          <FileUpload
            :umsoknType="4"               
            :umsoknId="umsokn.id"
            :filespace="3"
            :disable="props.disable"
            @uploaded="onFileUploaded"
          />

          <div class="error__text" v-if="v$.fileValidation.filerequired.$error">
            {{ t('common.val_required') }}
          </div>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style scoped>
.m-b-40 { margin-bottom: 40px; }
.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}
.error__text {
  color: #b00020;
  font-size: 0.875rem;
  margin-top: 4px;
}
</style>
